import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const Namespaces = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <section className="section">
      <h2 className="section-title">Namespaces</h2>
      <p className="text-gray-300 mb-6">
        Namespaces are containers for organizing documents and enabling document ingestion, search, and knowledge graph operations.
        Namespaces support role-based sharing with owners and members, allowing you to collaborate on knowledge bases.
        Members of projects that use a namespace automatically get read access to that namespace.
      </p>

      <EndpointCard
        method="POST"
        path="/namespaces/"
        title="Create Namespace"
        description="Creates a new namespace for organizing documents."
        tags={['Authentication: Required']}
        requestBody={{
          name: "my-namespace",
          description: "Optional description",
          data: {}
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "name": "my-namespace",
  "description": "Optional description",
  "data": {}
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>name</strong> (required): The name of the namespace<br/>
              <strong>description</strong> (optional): A description of the namespace<br/>
              <strong>data</strong> (optional): Additional metadata as a JSON object
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/namespaces/" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "my-namespace",
    "description": "Optional description",
    "data": {}
  }'`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/namespaces/"
        title="List Namespaces"
        description="Retrieves a paginated list of all namespaces."
        tags={['Authentication: Required', 'Pagination: Supported']}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/namespaces/" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/namespaces/{namespace_uuid}"
        title="Get Namespace"
        description="Retrieves detailed information about a specific namespace by its UUID."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>namespace_uuid</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the namespace</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/namespaces/{namespace_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "e1f2g3h4-i5j6-7890-klmn-op1234567890",
  "name": "my-namespace",
  "description": "Optional description",
  "data": {},
  "created_at": "2024-01-15T10:00:00Z"
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/namespaces/{namespace_uuid}/documents"
        title="Upload Document"
        description="Uploads and ingests a new document into a namespace asynchronously. Returns an ingestion task UUID to poll for completion status."
        tags={['Authentication: Required', 'Async: Yes']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>namespace_uuid</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the namespace</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <p className="text-sm text-gray-400 mb-2">Multipart form data with the file to upload.</p>
            <p className="text-xs text-gray-400 mt-2">
              <strong>Supported file types:</strong> .txt (PLAINTEXT), .md (MARKDOWN), .pdf (PDF)
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/namespaces/{namespace_uuid}/documents" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -F "file=@/path/to/document.pdf"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (202 Accepted):</h4>
            <CodeBlock code={`{
  "uuid": "{ingestion_task_uuid}",
  "document_uuid": "{document_uuid}",
  "status": "PENDING"
}`} />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>Note:</strong> The document is processed asynchronously. Use the returned <code className="bg-blue-900/50 px-1 py-0.5 rounded">uuid</code>
              (ingestion task UUID) to poll the <code className="bg-blue-900/50 px-1 py-0.5 rounded">/ingestionTasks/{'{task_uuid}'}</code> endpoint for completion status.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/namespaces/{namespace_uuid}/documents"
        title="List Documents in Namespace"
        description="Retrieves a list of all documents within a specific namespace."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>namespace_uuid</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the namespace</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/namespaces/{namespace_uuid}/documents" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`[
  {
    "uuid": "f1a2b3c4-d5e6-7890-abcd-ef1234567890",
    "namespace_uuid": "{namespace_uuid}",
    "document_type": "PDF",
    "status": "COMPLETED",
    "data": {
      "filename": "document.pdf"
    },
    "created_at": "2024-01-15T10:00:00Z"
  }
]`} />
          </div>
        </div>
      </EndpointCard>

      <div className="mt-8 mb-6">
        <h3 className="text-xl font-semibold text-gray-200 mb-4">Namespace Sharing & Member Management</h3>
        <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
          Namespaces support role-based sharing with two roles: <strong>owner</strong> and <strong>member</strong>.
          Owners can add and remove members, while members have read and write access to namespace resources.
          Only namespace owners can manage members. Additionally, users who are members of projects that use a namespace
          automatically get implicit read access to that namespace.
        </p>
      </div>

      <EndpointCard
        method="GET"
        path="/namespaces/{namespace_uuid}/members"
        title="List Namespace Members"
        description="Retrieves all members of a namespace with their roles. Returns user information including email and role."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/namespaces/{namespace_uuid}/members" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`[
  {
    "user_uuid": "{owner_uuid}",
    "user_email": "owner@example.com",
    "role": "owner",
    "created_at": "2024-01-15T10:00:00Z"
  },
  {
    "user_uuid": "{member_uuid}",
    "user_email": "member@example.com",
    "role": "member",
    "created_at": "2024-01-16T14:30:00Z"
  }
]`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/namespaces/{namespace_uuid}/members"
        title="Add Namespace Member"
        description="Adds a user as a member to a namespace. Only namespace owners can add members. The user must exist in the system (identified by email)."
        tags={['Authentication: Required', 'Owner Only']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' }
          }
        }}
        requestBody={{
          user_email: "member@example.com",
          role: "member"
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "user_email": "member@example.com",
  "role": "member"
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>user_email</strong> (required): Email address of the user to add as a member<br/>
              <strong>role</strong> (optional, default: "member"): Role to assign. Must be "owner" or "member"
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/namespaces/{namespace_uuid}/members" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_email": "member@example.com",
    "role": "member"
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (201 Created):</h4>
            <CodeBlock code={`{
  "user_uuid": "{member_uuid}",
  "user_email": "member@example.com",
  "role": "member",
  "created_at": "2024-01-16T14:30:00Z"
}`} />
          </div>

          <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
            <p className="text-sm text-yellow-200">
              <strong>Note:</strong> Only namespace owners can add members. If the user doesn't exist, you'll receive a 400 error.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="DELETE"
        path="/namespaces/{namespace_uuid}/members/{user_uuid}"
        title="Remove Namespace Member"
        description="Removes a user from a namespace. Only namespace owners can remove members."
        tags={['Authentication: Required', 'Owner Only']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' },
            user_uuid: { required: true, description: 'The unique identifier of the user to remove' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X DELETE "${BASE_URL}/namespaces/{namespace_uuid}/members/{user_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (204 No Content):</h4>
            <p className="text-sm text-gray-400">No response body for successful deletion.</p>
          </div>

          <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
            <p className="text-sm text-yellow-200">
              <strong>Note:</strong> Only namespace owners can remove members. Removing a member does not affect their
              implicit access through project membership.
            </p>
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default Namespaces;
